/**
 * Enhanced utility functions for authentication
 */

/**
 * Detects if the browser is in incognito/private browsing mode
 * This is used to adjust storage and cookie settings accordingly
 */
export const detectIncognitoMode = async () => {
  if (typeof window === "undefined") return false;

  // First check: Try writing to localStorage
  try {
    const testKey = "incognito_test";
    localStorage.setItem(testKey, "1");
    localStorage.removeItem(testKey);
  } catch (e) {
    // If writing to localStorage fails, we're likely in incognito
    return true;
  }

  // Second check: IndexedDB availability (often blocked in incognito)
  try {
    const db = window.indexedDB.open("test");
    if (!db) return true;
  } catch (e) {
    return true;
  }

  return false;
};

/**
 * Gets the appropriate storage mechanism based on browser capabilities
 * Automatically falls back to sessionStorage if localStorage is unavailable
 */
export const getAuthStorage = () => {
  // Try localStorage first
  try {
    const testKey = "auth_storage_test";
    localStorage.setItem(testKey, "1");
    localStorage.removeItem(testKey);
    return localStorage;
  } catch (e) {
    // Then try sessionStorage
    try {
      const testKey = "auth_storage_test";
      sessionStorage.setItem(testKey, "1");
      sessionStorage.removeItem(testKey);
      return sessionStorage;
    } catch (e) {
      console.warn("Both localStorage and sessionStorage unavailable", e);
      // Return a no-op storage object as a last resort
      return {
        getItem: () => null,
        setItem: () => null,
        removeItem: () => null,
      };
    }
  }
};

/**
 * Centralized function to check and get the current auth session
 * Use this instead of calling supabase.auth.getSession() directly
 * @param {Object} supabase - The supabase client instance
 * @returns {Promise<{session: Object|null, error: Error|null}>}
 */
export const getAuthSession = async (supabase) => {
  try {
    const { data, error } = await supabase.auth.getSession();

    if (error) {
      console.error("Error getting auth session:", error);
      return { session: null, error };
    }

    // If user is definitely invalid, return null session
    if (!data || !data.session || !data.session.user) {
      return { session: null, error: null };
    }

    // Extra check - get user details to verify session is truly valid
    // This helps prevent stale sessions that look valid but fail on use
    try {
      const { data: userData, error: userError } =
        await supabase.auth.getUser();

      if (userError || !userData || !userData.user) {
        console.warn(
          "Session exists but user info can't be retrieved, session may be invalid"
        );
        return { session: null, error: userError };
      }

      return { session: data.session, error: null };
    } catch (userCheckError) {
      console.error(
        "Error validating user during session check:",
        userCheckError
      );
      return { session: null, error: userCheckError };
    }
  } catch (error) {
    console.error("Unexpected error getting auth session:", error);
    return { session: null, error };
  }
};

/**
 * Centralized function to exchange code for session during PKCE flow
 * Handles rate limiting and common OAuth errors
 *
 * NOTE: We handle code exchange manually in AuthCallback.jsx instead of relying on
 * Supabase's automatic detectSessionInUrl feature to prevent duplicate token exchanges
 * which would cause "code verifier should be non-empty" errors.
 *
 * @param {Object} supabase - The supabase client instance
 * @param {string} code - OAuth code to exchange
 * @returns {Promise<{success: boolean, session: Object|null, error: Error|null, isRateLimit: boolean, isFlowStateError: boolean}>}
 */
export const exchangeCodeForSessionSafe = async (supabase, code) => {
  try {
    // Attempt the normal exchange flow
    const { data, error } = await supabase.auth.exchangeCodeForSession(code);

    if (error) {
      // Check for common error types
      const errorMessage = error.message || "";

      // Is this a rate limit error?
      if (
        errorMessage.includes("rate limit") ||
        error.status === 429 ||
        errorMessage.includes("too many requests")
      ) {
        console.warn("OAuth exchange rate limit detected:", error);
        return {
          success: false,
          isRateLimit: true,
          error,
        };
      }

      // Is this a flow state error?
      if (
        errorMessage.includes("flow_state") ||
        errorMessage.includes("pkce") ||
        errorMessage.includes("state") ||
        errorMessage.includes("code verifier")
      ) {
        console.warn("OAuth flow state error detected:", error);
        return {
          success: false,
          isFlowStateError: true,
          error,
        };
      }

      // Generic error
      console.error("Error exchanging code for session:", error);
      return { success: false, error };
    }

    // Success! We have a session
    return {
      success: true,
      session: data.session,
    };
  } catch (error) {
    console.error("Unexpected error exchanging code for session:", error);
    return { success: false, error };
  }
};

/**
 * Clears all OAuth-related state from localStorage and cookies
 * This is useful when encountering OAuth errors or when starting a new OAuth flow
 */
export const clearOAuthState = async (supabase) => {
  try {
    // Remove browser storage items used for OAuth
    localStorage.removeItem("supabase.auth.token");
    localStorage.removeItem("oauth_state");
    sessionStorage.removeItem("auth_callback_processed");
    sessionStorage.removeItem("auth_callback_timestamp");
    sessionStorage.removeItem("auth_callback_completed");

    // Clear cookies
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf("=");
      const name =
        eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();

      if (
        name.includes("supabase") ||
        name.includes("auth") ||
        name.includes("sb-") ||
        name.includes("oauth")
      ) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
      }
    }

    return true;
  } catch (err) {
    console.error("Error clearing OAuth state:", err);
    return false;
  }
};

/**
 * Function to prepare browser storage for a new OAuth login
 * Clears existing state and configures storage for the new flow
 * @returns {Promise<boolean>} Success status
 */
export const prepareForOAuthLogin = async () => {
  await clearOAuthState();

  // Wait a short while to ensure storage clearing completes
  await new Promise((resolve) => setTimeout(resolve, 100));

  return true;
};

/**
 * Gets the appropriate base domain for the current environment
 * Returns the correct domain to use for redirects and OAuth flows
 */
export const getBaseDomain = () => {
  if (typeof window === "undefined") return "artifact.chat"; // SSR case

  const hostname = window.location.hostname;

  // Handle localhost development
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return hostname + (window.location.port ? `:${window.location.port}` : "");
  }

  // Handle dev.artifact.chat
  if (hostname === "dev.artifact.chat") {
    return "dev.artifact.chat";
  }

  // Handle artifact.chat and its subdomains
  if (hostname.includes("artifact.chat")) {
    return hostname;
  }

  // Handle Netlify preview URLs (e.g., dev--artifactchat.netlify.app)
  if (hostname.includes("netlify.app")) {
    return hostname;
  }

  // Default fallback
  return "artifact.chat";
};

/**
 * Gets the appropriate redirect URL based on the current environment
 * Ensures consistent behavior across development and production
 */
export const getRedirectUrl = () => {
  if (typeof window === "undefined") return "/"; // SSR case

  // Get the protocol and base domain
  const protocol = window.location.protocol;
  const baseDomain = getBaseDomain();

  // Construct the redirect URL
  const redirectTo = `${protocol}//${baseDomain}/auth/callback`;

  return redirectTo;
};

/**
 * Gets the appropriate domain for cookies based on the current hostname
 * This is crucial for proper PKCE flow management
 */
export const getCookieDomain = () => {
  if (typeof window === "undefined") return null;

  const hostname = window.location.hostname;

  // Localhost should use null domain to work with cookies properly
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return null;
  }

  // Handle dev.artifact.chat specifically
  if (hostname === "dev.artifact.chat") {
    return "dev.artifact.chat";
  }

  // Handle artifact.chat and its subdomains
  if (hostname.includes("artifact.chat")) {
    return hostname;
  }

  // Handle Netlify preview URLs - use null to avoid cross-domain issues
  if (hostname.includes("netlify.app")) {
    return null;
  }

  // Get the base domain (for artifact.chat, returns artifact.chat)
  return hostname;
};

/**
 * Logs the current OAuth configuration for debugging
 */
export const logOAuthConfig = () => {
  const config = {
    pkceEnabled: true,
    currentUrl: typeof window !== "undefined" ? window.location.href : "N/A",
    redirectUrl: getRedirectUrl(),
    cookieDomain: getCookieDomain(),
    isLocalhost:
      typeof window !== "undefined" &&
      (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1"),
  };

  return config;
};

/**
 * Helper function to detect if the current URL has an OAuth error
 */
export const hasOAuthError = () => {
  if (typeof window === "undefined") return false;

  const url = new URL(window.location.href);
  return (
    url.searchParams.has("error") || url.searchParams.has("error_description")
  );
};

/**
 * Helper function to extract OAuth error from URL
 */
export const getOAuthError = () => {
  if (typeof window === "undefined") return null;

  const url = new URL(window.location.href);
  const error = url.searchParams.get("error");
  const description = url.searchParams.get("error_description");

  if (!error) return null;

  return {
    error,
    description: description || "No description provided",
  };
};

/**
 * Checks if we're likely being rate limited by the OAuth provider
 * and manages the rate limit tracking
 */
export const checkAndHandleRateLimit = () => {
  const recentExchangeAttempts = parseInt(
    localStorage.getItem("oauth_exchange_attempts") || "0"
  );
  const lastExchangeTime = parseInt(
    localStorage.getItem("oauth_last_exchange_time") || "0"
  );
  const now = Date.now();

  // If we've had multiple recent attempts, we might be rate limited
  const potentialRateLimit =
    recentExchangeAttempts > 3 && now - lastExchangeTime < 30000;

  if (potentialRateLimit) {
    console.warn("Potential rate limiting detected - adding cooldown period");

    // Reset the counter to avoid cascading rate limits
    localStorage.setItem("oauth_exchange_attempts", "0");
    localStorage.setItem("oauth_rate_limited", "true");
    localStorage.setItem("oauth_rate_limit_time", String(now));

    // Schedule to clear the rate limit flag after the cooldown period
    setTimeout(() => {
      localStorage.removeItem("oauth_rate_limited");
      localStorage.removeItem("oauth_rate_limit_time");
    }, 60000); // 1 minute cooldown

    return true;
  }

  // Check if we're in a cooldown period
  const isRateLimited = localStorage.getItem("oauth_rate_limited") === "true";
  const rateLimitTime = parseInt(
    localStorage.getItem("oauth_rate_limit_time") || "0"
  );

  // If it's been more than 1 minute since the rate limit, clear the flag
  if (isRateLimited && now - rateLimitTime > 60000) {
    localStorage.removeItem("oauth_rate_limited");
    localStorage.removeItem("oauth_rate_limit_time");
    return false;
  }

  return isRateLimited;
};

/**
 * Gets the appropriate message for the current OAuth state
 */
export const getOAuthStateMessage = () => {
  // Check if we're rate limited
  if (checkAndHandleRateLimit()) {
    return "Authentication is temporarily unavailable due to too many attempts. Please wait a minute and try again.";
  }

  // Check if we have an OAuth error in the URL
  if (hasOAuthError()) {
    const error = getOAuthError();

    // Customize message based on error type
    if (
      error.error === "invalid_request" &&
      error.description.includes("flow state")
    ) {
      return "Your authentication session expired. Please try signing in again.";
    }

    return `Authentication error: ${error.description || error.error}`;
  }

  return null;
};

/**
 * Checks if a user has completed onboarding
 * @param {Object} profile - User profile object from database
 * @returns {boolean} Whether onboarding is complete
 */
export const isOnboardingComplete = (profile) => {
  if (!profile) return false;

  // Primary check - use the top-level field
  const hasCompletedOnboarding = !!profile.onboarding_completed;

  // Return the result from the top-level field
  return hasCompletedOnboarding;
};

/**
 * A reliable way to check if a user is authenticated
 * This checks both the auth store state and the active session
 */
export const isUserAuthenticated = async (supabase, authStore) => {
  // First check the authStore's user state
  const user = authStore.getState().user;
  if (!user || !user.id) {
    return false;
  }

  // Double-check with a live session check
  try {
    const { session, error } = await getAuthSession(supabase);

    if (error || !session) {
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error checking authentication status:", error);
    return false;
  }
};

/**
 * Clear all auth-related storage and force a fresh login
 * Useful for troubleshooting and recovering from loop issues
 */
export const forceAuthReset = () => {
  try {
    // Clear all storage
    localStorage.removeItem("auth-storage");
    localStorage.removeItem("supabase.auth.token");
    sessionStorage.removeItem("auth_callback_processed");
    sessionStorage.removeItem("auth_callback_timestamp");
    sessionStorage.removeItem("auth_callback_completed");
    sessionStorage.removeItem("auth_init_cache");
    sessionStorage.removeItem("auth_init_timestamp");
    sessionStorage.removeItem("auth_init_error");

    // Clear cookies
    document.cookie.split(";").forEach((c) => {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });

    // Force reload to the login page
    window.location.href = "/auth/login?reset=true";
    return true;
  } catch (error) {
    console.error("Error during auth reset:", error);
    return false;
  }
};

export default {
  clearOAuthState,
  getRedirectUrl,
  getBaseDomain,
  getCookieDomain,
  logOAuthConfig,
  hasOAuthError,
  getOAuthError,
  checkAndHandleRateLimit,
  getOAuthStateMessage,
  prepareForOAuthLogin,
  getAuthSession,
  exchangeCodeForSessionSafe,
  isOnboardingComplete,
  isUserAuthenticated,
  forceAuthReset,
};
